import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";

import { Subscriber } from "rxjs";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";

import { VenueService } from "../venue.service";
import { Pitch, PitchData } from "../../shared/model/pitch";
import { Venue } from "../../shared/model/venue";
import { ApiEndpoints } from "../../shared/apiEndpoints";
import { PitchCollection } from "../../shared/model/pitchCollection";
import { SearchListLinkService } from "./../../shared/search.list.link.service";
import { SearchService } from "app/search-results/search.service";

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.Emulated,
  selector: "mlp-venue-sidebar",
  templateUrl: "venue-sidebar.component.html",
  styleUrls: ["./venue-sidebar.component.scss"],
})
export class VenueSidebarComponent implements OnInit, OnDestroy {
  isBrowser: boolean = isPlatformBrowser(this.platformId);
  private subscribed: Subscriber<any>;
  public similarPitchData: Array<{ pitch: Pitch; venue: Venue }> = [];
  public color: string;

  public isPhoneNumberHidden: boolean = true;
  public sport: string = "";

  @Input() pitchesWithSameSport: Array<Pitch> = [];
  @Input() openMapEvent = new EventEmitter();

  constructor(
    @Inject(PLATFORM_ID) private platformId,
    @Inject(DOCUMENT) private readonly document,
    private router: Router,
    public venueService: VenueService,
    public apiEndpoints: ApiEndpoints,
    public searchListLinkService: SearchListLinkService,
    public searchService: SearchService
  ) {
    this.subscribed = this.venueService.pitchDataChange.subscribe(
      (returnedData: PitchData) => {
        this.getSimilarVenues(this.venueService.pitchData);
        this.sport = returnedData.data.attributes.sport;
        this.isPhoneNumberHidden = true;
      }
    );
  }

  ngOnInit() {
    if (this.venueService.pitchData) {
      this.getSimilarVenues(this.venueService.pitchData);
    }
  }

  ngOnDestroy() {
    this.subscribed.unsubscribe();
  }

  scrollToTheTop() {
    window.scrollTo(0, 0);
  }

  revealPhoneNumber() {
    this.isPhoneNumberHidden = false;
  }

  viewMap() {
    document.getElementById("mlp-map-scroll-to").scrollIntoView();

    // adjust fixed header margin
    window.scrollBy(0, -55);
  }

  isVenueHomepage(pitches: Array<Pitch>): boolean {
    return !pitches.some((pitch) => this.router.url === pitch.links.weblink);
  }

  private getSimilarVenues(pitchData: PitchData) {
    const is_partner = pitchData.data.attributes.mlp ? 0 : 1;
    this.apiEndpoints
      .callMlpApiGetPitchCollectionFromLocation(pitchData, 6, is_partner)
      .subscribe(
        (pitches: PitchCollection) => {
          this.similarPitchData = [];

          pitches.data.forEach((pitch: Pitch) => {
            if (pitch.id != pitchData.data.id) {
              let venueFound;

              pitches.included.forEach((venue: Venue) => {
                if (venue.id == pitch.relationships.venues.data.id) {
                  venueFound = venue;
                }
              });

              this.similarPitchData.push({
                pitch: pitch,
                venue: venueFound,
              });
            }
          });
        },
        (error) => {
          console.log(error);
        }
      );
  }

  lowercase(value: string) {
    if (value) {
      return value.toLowerCase();
    }
  }

  milesAway(venue: Venue): string {
    const dist = this.getDistanceFromLatLonInMiles(
      this.venueService.venueData.data.attributes.latitude,
      this.venueService.venueData.data.attributes.longitude,
      venue.attributes.latitude,
      venue.attributes.longitude
    );

    const distRounded = Math.round(dist * 10 + Number.EPSILON) / 10;

    return !isNaN(distRounded)
      ? `${distRounded} ${distRounded === 1 ? "mile" : "miles"} away`
      : "";
  }

  openWidget(widgetUrl: string) {
    this.document.defaultView.open(widgetUrl, "_blank");
  }

  private getDistanceFromLatLonInMiles(lat1, lon1, lat2, lon2): number {
    let mileConversion = 0.621371;
    let earthRadius = 6371; // Radius of the earth in km
    let dLat = this.deg2rad(lat2 - lat1);
    let dLon = this.deg2rad(lon2 - lon1);
    let a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c * mileConversion;
  }

  private deg2rad(deg) {
    return deg * (Math.PI / 180);
  }
}
